/**
 * The `.metadata` rows: which project, estimate, GCGR, contracting set and cost model the
 * workbook currently holds. Report generation reads these for identity only — it asks which
 * estimate is loaded, then fetches the data itself. They are bookkeeping about sourced state,
 * not reports.
 *
 * The Legacy section is the pre-migration `__GCGR` / `__Report` surface. Every write there is
 * guarded on the sheet already existing, so a workbook built after the migration never gets
 * one. Retiring legacy means deleting that section and the two calls into it.
 */
import {
  fetchDesignStages,
  fetchEstimateMilestone,
  fetchGCGRsByProjectId,
  fetchInternalEstimateMarkupsByProjectId,
  fetchProjectById,
} from "../../api";
import { serializeErrorForWarning } from "../../errors";
import { BidGroup } from "../../interfaces/BidTabs";
import { ContractingReport } from "../../interfaces/ContractingReport";
import { CostModel, CostModelTotalsLite } from "../../interfaces/CostModel";
import { CostModelReport } from "../../interfaces/CostModelReport";
import { EstimateData } from "../../interfaces/EstimateData";
import { GCGRReport } from "../../interfaces/GCGRReport";
import { GCGR } from "../../interfaces/GCGRs";
import { Project } from "../../interfaces/Project";
import { costModelLabel } from "../../utils/format";
import { loadedDatasourceStore } from "../../utils/storageKeys";
import { getTenant } from "../../utils/tenant";
import { getTableData, loadDataIntoSheet } from "../workbook";
import { refillPrintLayouts } from "../workbook/printLayout";
import { estimateStaleCheckBaselines, gcgrStaleCheckBaselines, maxServerUpdatedAt } from "./staleness";
import {
  deleteCardSummary,
  saveContractingCardSummary,
  saveCostModelCardSummary,
  saveEstimateCardSummary,
  saveGcgrCardSummary,
} from "./cardSummaries";
import Logger from "../../clients/sentry";

/* -------------------------------------------------------------------------- */
/* Clearing                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The server's `updated_at` is the change-check baseline, read back by `excelDateToEpochMs`, so
 * it's stored in UTC: a workbook opened in another time zone must read the same instant.
 */
const UTC_COLUMNS = ["updated_at"];

export const REPORT_METADATA_TABLES = [
  "EstimateReport",
  "GCGRReport",
  "ContractingReport",
  "CostModelReport",
  "Report",
  "ProjectReport",
  "GCGR",
];

/* -------------------------------------------------------------------------- */
/* Project                                                                   */
/* -------------------------------------------------------------------------- */

/** What `tabulateProjectReportData` needs populated on the project. */
export const PROJECT_REPORT_POPULATE = ["owner", "opportunity", "region"];

export const clearReportTables = async (...tableNames: string[]): Promise<void> => {
  await Excel.run(async (context) => {
    for (const tableName of tableNames) {
      const table = context.workbook.tables.getItemOrNullObject(tableName);
      await context.sync();
      if (!table.isNullObject) {
        table.getDataBodyRange().clear(Excel.ClearApplyTo.contents);
      }
    }
    await context.sync();
  });
};

export const clearGCGRReportTable = async (): Promise<void> => {
  await loadDataIntoSheet([], "GCGRReport", { sheetName: ".metadata", startCell: "A7" });
  await deleteCardSummary("gcgr");
  loadedDatasourceStore.remove("selectedGcgrId");
  await syncLegacyReportTable();
  await clearLegacyGcgrTable();
};

export const clearEstimateReportTable = async (): Promise<void> => {
  await loadDataIntoSheet([], "EstimateReport", { sheetName: ".metadata", startCell: "A4" });
  await deleteCardSummary("estimate");
  loadedDatasourceStore.remove("selectedEstimateId");
  await clearLegacyReportTable();
  await refillPrintLayouts();
};

export const clearContractingReportTable = async (): Promise<void> => {
  await loadDataIntoSheet([], "ContractingReport", { sheetName: ".metadata", startCell: "A10" });
  await deleteCardSummary("contracting");
  loadedDatasourceStore.remove("contractingLoaded");
};

export const clearCostModelReportTable = async (): Promise<void> => {
  await loadDataIntoSheet([], "CostModelReport", { sheetName: ".metadata", startCell: "A13" });
  await deleteCardSummary("cost-model");
  loadedDatasourceStore.remove("selectedCostModelId");
};

/**
 * Writes `ProjectReport` from the project, fetched with `PROJECT_REPORT_POPULATE` unless the
 * caller already has it that way. Every datasource refresh writes it first, so a project edit
 * reaches the workbook with whichever refresh comes next.
 */
export const tabulateProjectReportData = async (projectId: string, fetched?: Project): Promise<void> => {
  const tenant = getTenant() ?? "";

  const baseProjectData: Record<string, string> = {
    report_type: "Project",
    last_updated: new Date().toISOString(),
    project_id: projectId,
    project_name: "",
    region_id: "",
    region_name: "",
    creator_name: "",
    creator_email: "",
    opportunity_name: "",
    opportunity_number: "",
    client_name: "",
    industry: "",
    task_number: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    tenant,
  };

  try {
    const projectResult = fetched
      ? { success: true as const, data: fetched }
      : await fetchProjectById(projectId, { populate: PROJECT_REPORT_POPULATE });
    if (!projectResult.success) throw projectResult.error;

    const project: Project = (projectResult.data || {}) as Project;

    let opportunity_number = "";
    let opportunity_name = "";
    if (project.opportunity && typeof project.opportunity === "object") {
      opportunity_number = project.opportunity?.opty_number || "";
      opportunity_name = project.opportunity?.name || "";
    }
    let creator_name = "";
    let creator_email = "";
    if (project.owner && typeof project.owner === "object") {
      creator_name = project.owner?.full_name || "";
      creator_email = project.owner?.email || "";
    }
    const region = typeof project.region === "object" && project.region !== null ? project.region : null;

    await loadDataIntoSheet(
      [
        {
          ...baseProjectData,
          project_id: project?.id || projectId,
          project_name: project?.name || "",
          region_id: region?.id || (typeof project?.region === "string" ? project.region : ""),
          region_name: region?.name || "",
          creator_name,
          creator_email,
          opportunity_name,
          opportunity_number,
          client_name: project?.client_name || "",
          industry: project?.industry || "",
          task_number: project?.task_number || "",
          address: project?.address || "",
          city: project?.city || "",
          state: project?.state || "",
          zip: project?.zip || "",
        },
      ],
      "ProjectReport",
      { sheetName: ".metadata", startCell: "A1" }
    );
  } catch (error) {
    Logger.warn("Error with PROJECT REPORT etl enrichment", serializeErrorForWarning(error));
    // Keep the existing row when it already holds this project — overwriting with blank base
    // data on a transient fetch failure would wipe report headers and export filenames
    const existing = (await getTableData("ProjectReport"))[0];
    if (existing?.project_id !== projectId) {
      await loadDataIntoSheet([baseProjectData], "ProjectReport", {
        sheetName: ".metadata",
        startCell: "A1",
        utcColumns: UTC_COLUMNS,
      });
    }
  }
  loadedDatasourceStore.set("selectedProjectId", projectId);
  // Keep the legacy combined __Report data table in sync for old workbooks (no-op on modern ones).
  await syncLegacyReportTable();
  await refillPrintLayouts();
};

/* -------------------------------------------------------------------------- */
/* Estimate                                                                  */
/* -------------------------------------------------------------------------- */

export const tabulateEstimateReportData = async (estimateData: EstimateData): Promise<void> => {
  const baseEstimateData: Record<string, string> = {
    report_type: "Estimate",
    // Client refresh stamp — what reports print as "last updated".
    last_updated: new Date().toISOString(),
    estimate_id: estimateData?.estimate?.id || "",
    estimate_name: estimateData?.estimate?.name || "",
    milestone: "",
    milestone_description: "",
    design_stage: "",
    ...Object.fromEntries(
      Object.entries(estimateData.projectMetadata || {}).map(([key, value]) => [`meta_${key}`, value || ""])
    ),
    project_id: estimateData?.projectId || "",
    // Server updated_at — the staleness baseline (compared with the server's current value).
    updated_at: estimateData?.estimate?.updated_at || "",
    due_date: estimateData?.estimate?.due_date || "",
    ...estimateStaleCheckBaselines(estimateData),
  };

  try {
    const milestoneId = estimateData.estimate.milestone;
    const [milestoneResult, designStageResult] = await Promise.all([
      milestoneId
        ? fetchEstimateMilestone(estimateData.projectId, milestoneId)
        : Promise.resolve({ success: true as const, data: undefined }),
      fetchDesignStages(),
    ]);
    if (!milestoneResult.success) throw milestoneResult.error;
    if (!designStageResult.success) throw designStageResult.error;

    const designStages = designStageResult.data || [];
    const milestone = milestoneResult.data;
    const designStage = designStages.find((ds) => ds.id === milestone?.design_stage);

    let milestoneName = "";
    let milestoneDescription = "";
    if (milestone && typeof milestone === "object") {
      milestoneName = milestone?.name || "";
      milestoneDescription = milestone?.description ?? "";
    }
    let designStageName = "";
    if (designStage && typeof designStage === "object") {
      designStageName = designStage?.name || "";
    }

    await loadDataIntoSheet(
      [
        {
          ...baseEstimateData,
          milestone: milestoneName,
          milestone_description: milestoneDescription,
          design_stage: designStageName,
        },
      ],
      "EstimateReport",
      { sheetName: ".metadata", startCell: "A4", utcColumns: UTC_COLUMNS }
    );
    await saveEstimateCardSummary(estimateData.estimate, milestoneName, baseEstimateData.last_updated);
  } catch (error) {
    Logger.warn("Error with ESTIMATE REPORT etl enrichment", serializeErrorForWarning(error));
    // Only the milestone/design-stage enrichment failed — the export data itself is fresh.
    // Carry the previous enrichment fields over but still write baseEstimateData with its fresh
    // stamps: preserving the old updated_at would make the staleness check re-trigger a full refresh
    // on every report click until enrichment recovers.
    const existing = (await getTableData("EstimateReport"))[0];
    const previousEnrichment =
      existing?.estimate_id === baseEstimateData.estimate_id
        ? {
            milestone: (existing.milestone as string) ?? "",
            milestone_description: (existing.milestone_description as string) ?? "",
            design_stage: (existing.design_stage as string) ?? "",
          }
        : {};
    await loadDataIntoSheet([{ ...baseEstimateData, ...previousEnrichment }], "EstimateReport", {
      sheetName: ".metadata",
      startCell: "A4",
      utcColumns: UTC_COLUMNS,
    });
    const previousMilestone =
      existing?.estimate_id === baseEstimateData.estimate_id ? ((existing.milestone as string) ?? "") : "";
    await saveEstimateCardSummary(estimateData.estimate, previousMilestone, baseEstimateData.last_updated);
  }
  loadedDatasourceStore.set("selectedEstimateId", baseEstimateData.estimate_id);

  // Keep the legacy combined __Report data table in sync for old workbooks (no-op on modern ones).
  await syncLegacyReportTable();
  await refillPrintLayouts();
};

/* -------------------------------------------------------------------------- */
/* GCGR                                                                      */
/* -------------------------------------------------------------------------- */

export const tabulateGCGRReportData = async (projectId: string, gcgr: GCGR): Promise<void> => {
  const gcgrReport: GCGRReport = {
    report_type: "GCGR",
    last_updated: new Date().toISOString(),
    gcgr_id: gcgr.id,
    gcgr_name: gcgr.name,
    estimate: gcgr.estimate,
    precon_start: gcgr.precon_start,
    precon_finish: gcgr.precon_finish,
    construction_start: gcgr.construction_start,
    construction_finish: gcgr.construction_finish,
    escalation_date: gcgr.escalation_date,
    pto: gcgr.pto,
    it_week: gcgr.it_week,
    iphone_week: gcgr.iphone_week,
    gas_week: gcgr.gas_week,
    estimating_multiplier: gcgr.estimating_multiplier,
    construction_multiplier: gcgr.construction_multiplier,
    precon_raises: gcgr.precon_raises,
    construction_raises: gcgr.construction_raises,
    contract_value: gcgr.contract_value,
    updated_by: gcgr.updated_by,
    project_id: projectId,
    // Server updated_at — the staleness baseline (compared with the server's current value).
    updated_at: gcgr.updated_at,
    ...gcgrStaleCheckBaselines(gcgr),
  };
  // ???: Do we want to add multiple GCGRs to the GCGRReport table? Currently, the table is scoped to the single resolved GCGR, but the GCGR data table holds all active GCGRs.
  await loadDataIntoSheet([gcgrReport], "GCGRReport", {
    sheetName: ".metadata",
    startCell: "A7",
    utcColumns: UTC_COLUMNS,
  });
  await saveGcgrCardSummary(gcgr, gcgrReport.last_updated);
  loadedDatasourceStore.set("selectedGcgrId", gcgr.id);
  await syncLegacyGcgrTable(projectId, gcgr);
  await syncLegacyReportTable();
};

/* -------------------------------------------------------------------------- */
/* Contracting                                                               */
/* -------------------------------------------------------------------------- */

export const tabulateContractingReportData = async (projectId: string, bidGroups: BidGroup[]): Promise<void> => {
  const markupsResult = await fetchInternalEstimateMarkupsByProjectId(projectId);
  const markups = markupsResult.success ? (markupsResult.data ?? []) : [];
  const projected_buy = bidGroups.reduce((sum, bg) => sum + (bg.projected_buy ?? 0), 0);
  const budget = bidGroups.reduce((sum, bg) => sum + (bg.budget ?? 0), 0);
  const buy_value = bidGroups.reduce((sum, bg) => sum + (bg.buy_value ?? 0), 0);
  const award = bidGroups.some((bg) => bg.award);
  const lock = bidGroups.some((bg) => bg.lock);
  const buy_date = bidGroups.reduce((latest, bg) => (bg.buy_date > latest ? bg.buy_date : latest), "");
  const contractingReport: ContractingReport = {
    report_type: "Contracting",
    // Client refresh stamp — what reports print as "last updated".
    last_updated: new Date().toISOString(),
    project_id: projectId,
    projected_buy,
    budget,
    award,
    lock,
    buy_date: buy_date || undefined,
    buy_value,
    // Server updated_at, newest across bid groups and markups — the staleness baseline, matching
    // what getContractingServerState reads.
    updated_at: maxServerUpdatedAt([...bidGroups, ...markups]),
    bid_group_count: bidGroups.length,
    markup_count: markups.length,
  };
  await loadDataIntoSheet([contractingReport], "ContractingReport", {
    sheetName: ".metadata",
    startCell: "A10",
    utcColumns: UTC_COLUMNS,
  });
  await saveContractingCardSummary(bidGroups, contractingReport.last_updated);
  loadedDatasourceStore.set("contractingLoaded", "true");
};

/* -------------------------------------------------------------------------- */
/* Cost model                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Every field comes off the cost model record itself, so the row is written with the same columns
 * every time. A second write with a different set would delete the columns it omitted.
 */
export const tabulateCostModelReportData = async (
  projectId: string,
  costModel: CostModel,
  totals: CostModelTotalsLite
): Promise<void> => {
  const costModelReport: CostModelReport = {
    report_type: "CostModel",
    // Client refresh stamp — what reports print as "last updated", as on the sibling report rows.
    last_updated: new Date().toISOString(),
    cost_model_id: costModel.id,
    cost_model_name: costModelLabel(costModel),
    created_at: costModel.created_at,
    lock: costModel.lock,
    project_id: projectId,
    // Server updated_at, as on the sibling report rows.
    updated_at: costModel.updated_at,
  };
  await loadDataIntoSheet([costModelReport], "CostModelReport", {
    sheetName: ".metadata",
    startCell: "A13",
    utcColumns: UTC_COLUMNS,
  });
  await saveCostModelCardSummary(costModel, totals, costModelReport.last_updated);
  loadedDatasourceStore.set("selectedCostModelId", costModel.id);
};

/* -------------------------------------------------------------------------- */
/* Legacy — __GCGR / __Report, pre-migration workbooks only                  */
/* -------------------------------------------------------------------------- */

/**
 * Mirror the active GCGRs into the legacy `__GCGR` table. Unlike the `.metadata` GCGRReport row,
 * which is scoped to the single resolved GCGR, this table holds them all — legacy formulas
 * reference them by row.
 */
export const syncLegacyGcgrTable = async (projectId: string, gcgr: GCGR): Promise<void> => {
  let gcgrSheetExists = false;
  await Excel.run(async (context) => {
    const gcgrSheet = context.workbook.worksheets.getItemOrNullObject("__GCGR");
    await context.sync();
    gcgrSheetExists = !gcgrSheet.isNullObject;
  });
  if (!gcgrSheetExists) return;

  // On a failed list fetch keep the existing rows — writing [gcgr] would shrink the table.
  const gcgrsResult = await fetchGCGRsByProjectId(projectId);
  if (!gcgrsResult.success) return;

  const rows = gcgrsResult.data?.length ? gcgrsResult.data : [gcgr];
  // The selected GCGR may be inactive, and so absent from the active list. Include it
  // regardless, or the table would omit the one GCGR its own metadata row names.
  const visibleGcgrs = rows.some((g) => g.id === gcgr.id) ? rows : [...rows, gcgr];
  await loadDataIntoSheet(visibleGcgrs, "GCGR", { dateOnly: true, addLastUpdated: true });
};

export const clearLegacyGcgrTable = async (): Promise<void> => {
  await loadDataIntoSheet([], "GCGR");
};

export const clearLegacyReportTable = async (): Promise<void> => {
  await loadDataIntoSheet([], "Report", { sheetName: "__Report", startCell: "A1" });
};

const writeLegacyReportGcgrId = async (gcgrId: string, columns: string[]): Promise<void> => {
  if (!columns.includes("gcgr_id")) return;
  await Excel.run(async (context) => {
    const table = context.workbook.tables.getItem("Report");
    table.rows.load("count");
    await context.sync();
    if (table.rows.count > 0) {
      table.columns.getItem("gcgr_id").getDataBodyRange().getCell(0, 0).values = [[gcgrId]];
    } else if (gcgrId) {
      table.rows.add(undefined, [columns.map((name) => (name === "gcgr_id" ? gcgrId : ""))]);
    }
    await context.sync();
  });
};

/**
 * Repopulate the legacy combined __Report data table (project + estimate columns) from the current
 * .metadata ProjectReport/EstimateReport rows — but ONLY when an old workbook actually has __Report.
 * Old custom-report formulas reference __Report by cell, so any flow that rewrites or CLEARS the
 * .metadata report tables must keep __Report populated instead of leaving it emptied (main never
 * emptied __Report — it only ever wrote to it). No-op on modern workbooks that never had the table.
 *
 * Existing __Report columns are preserved (blanked) before overlaying the fresh values, so on a
 * project-only refresh the estimate columns stay present-but-empty (formulas resolve to "" instead
 * of #REF). Pass estimateData when an estimate is loaded so the legacy gcgr_id column carries
 * estimate.gcgr; omit it for a project-only refresh / project switch.
 */
export const syncLegacyReportTable = async (): Promise<void> => {
  let reportExists = false;
  let existingColumns: string[] = [];
  await Excel.run(async (context) => {
    const reportSheet = context.workbook.worksheets.getItemOrNullObject("__Report");
    await context.sync();
    if (reportSheet.isNullObject) return;
    const t = reportSheet.tables.getItemOrNullObject("Report");
    await context.sync();
    if (t.isNullObject) return;
    reportExists = true;
    t.columns.load("items/name");
    await context.sync();
    existingColumns = t.columns.items.map((c) => c.name);
  });
  if (!reportExists) return;

  const projectData = await getTableData("ProjectReport");
  const estimateReportData = await getTableData("EstimateReport");
  const gcgrReportData = await getTableData("GCGRReport");
  const gcgrId = (gcgrReportData[0]?.gcgr_id as string | undefined) ?? "";
  // No project or estimate row to rebuild __Report from (a full wipe, or a GCGR loaded on its own):
  // leave the rest of the row as it is and update only its gcgr_id.
  if (projectData.length === 0 && estimateReportData.length === 0) {
    await writeLegacyReportGcgrId(gcgrId, existingColumns);
    return;
  }

  // Preserve every existing __Report column (blanked) so old formulas referencing an estimate column
  // resolve to empty instead of #REF when only a project is loaded, then overlay the fresh values.
  const blanked = Object.fromEntries(existingColumns.map((name) => [name, ""]));
  const combined = {
    ...blanked,
    ...(projectData[0] ?? {}),
    ...(estimateReportData[0] ?? {}),
    // Keep the Report[gcgr_id] column PRESENT so legacy workbook formulas resolve to empty instead of
    // #REF when no GCGR is loaded.
    gcgr_id: gcgrId,
  };
  await loadDataIntoSheet([combined], "Report", { sheetName: "__Report", startCell: "A1" });
};
